/**
 * WebVariantCard.tsx
 *
 * Web-optimized UnifiedMediaCard renderer with hover preview, PiP-safe overlays,
 * and in-card player mount behavior.
 */

import Image from "next/image";
import type React from "react";
import type { UnifiedMediaCardProps } from "../../../types/livelanding.types";
import { thumbnailLoader } from "../../../utils/image";
import { timeSinceEnded as utilTimeSinceEnded } from "../../../utils/mediaUtils";
import { LiveBadgeIcon, PlayIcon } from "../../Icons/MediaIcons";
import { ShadowSpinner } from "../../Loader/ShadowSpinner";
import { VideoPlayer } from "../../VideoPlayer/VideoPlayer";
import { PipOverlayLabel } from "../PipOverlayLabel";
import { CardHoverPreview, getAnimatedPosterUrl } from "./CardHoverPreview";
import { formatViews, getOverlayOpacityClass } from "./cardDisplayUtils";

type WebVariantCardProps = {
  sectionId: UnifiedMediaCardProps["sectionId"];
  encodedId?: string;
  pipThumbnailOverlayHidden: boolean;
  thumbnail: string;
  video: UnifiedMediaCardProps["video"];
  viewsCount: number;
  effectiveIsLive: boolean;
  timeSinceEndedWeb: UnifiedMediaCardProps["timeSinceEndedWeb"];
  isPlayerActive: boolean;
  isLoading: boolean;
  isCardHovered: boolean;
  isPreviewReady: boolean;
  isPreviewLoading: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  onPreviewReady: () => void;
};

/**
 * Renders web variant card with animated preview and click-to-open player.
 */
export function WebVariantCard({
  sectionId,
  encodedId,
  pipThumbnailOverlayHidden,
  thumbnail,
  video,
  viewsCount,
  effectiveIsLive,
  timeSinceEndedWeb,
  isPlayerActive,
  isLoading,
  isCardHovered,
  isPreviewReady,
  isPreviewLoading,
  videoRef,
  handleMouseEnter,
  handleMouseLeave,
  handleClick,
  handleKeyDown,
  onPreviewReady,
}: Readonly<WebVariantCardProps>) {
  const overlayId = `${sectionId}-overlay-${encodedId}`;
  const trailerId = `${sectionId}-trailer-${encodedId}`;
  const animatedPoster = getAnimatedPosterUrl(video);
  const shouldRevealPreview = isCardHovered && isPreviewReady && !pipThumbnailOverlayHidden;
  const overlayOpacityClass = getOverlayOpacityClass(
    pipThumbnailOverlayHidden,
    shouldRevealPreview
  );
  const showHoverPreview = isCardHovered && !pipThumbnailOverlayHidden;

  return (
    <button
      type="button"
      id={encodedId}
      className="font-inherit group flex-[0_0_210px] cursor-pointer overflow-hidden rounded-lg border-0 bg-white p-0 text-left max-[1200px]:flex-[0_0_180px] max-[768px]:flex-[0_0_120px] max-[420px]:flex-[0_0_109px]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid="video-card"
    >
      <div className="relative aspect-[9/16.3] max-h-[380px] max-w-[214px] overflow-hidden rounded-lg bg-black max-[1200px]:max-h-[320px] max-[1200px]:max-w-[180px] max-[420px]:max-h-[300px] max-[420px]:max-w-[160px] max-md:max-h-[300px] max-md:max-w-[160px] xl:max-h-[380px] xl:max-w-[214px]">
        <div className="absolute top-0 right-0 left-0 z-3 flex items-center justify-between bg-gradient-to-b from-black/60 to-transparent !p-[4px] lg:!p-[8px]">
          {effectiveIsLive ? (
            <span className="mr-[11px] flex items-center gap-1 rounded-[2px] bg-[#ff0000] px-1 py-0.5 !text-[10px] font-medium text-white uppercase md:!text-[14px]">
              <LiveBadgeIcon />
              LIVE
            </span>
          ) : (
            <span className="font-rubik rounded-[2px] bg-[#1A1A1AB2] !px-[5px] !py-[2px] text-[10px] font-medium text-white sm:!px-[6px] sm:!py-[2px] sm:!text-[11px] md:!px-[6px] md:!py-[2px] md:!text-[12px] xl:!px-[6px] xl:!py-[2px] xl:!text-[14px]">
              {(timeSinceEndedWeb ?? utilTimeSinceEnded)(video?.scheduled_at ?? null)}
            </span>
          )}
          {viewsCount > 0 && (
            <span className="flex items-center gap-1 rounded-[2px] bg-[#1A1A1AB2] px-[6px] py-[2px] text-[10px] font-medium text-white md:text-[12px] xl:text-[14px]">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 5C7 5 3 9 2 12c1 3 5 7 10 7s9-4 10-7c-1-3-5-7-10-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z"
                  fill="white"
                />
              </svg>
              {formatViews(viewsCount)}
            </span>
          )}
        </div>

        <div className="absolute top-0 left-0 h-full w-full">
          <CardHoverPreview
            isActive={showHoverPreview}
            animatedPoster={animatedPoster}
            trailerUrl={video.trailer?.file_url ?? undefined}
            captionsUrl={video.trailer?.captions_url ?? undefined}
            videoRef={videoRef}
            alt={video.event_name ? `${video.event_name} preview` : "Video preview"}
            trailerId={trailerId}
            videoClassName="absolute inset-0 z-[1] m-auto h-[380px] w-[214px] object-cover"
            onReady={onPreviewReady}
          />
          {isPlayerActive ? (
            <VideoPlayer
              className="h-full w-full"
              videoId={video.trailer?.encoded_id ?? ""}
              autoplay={false}
              maxVideos={1}
              playerCaptions={true}
              productCard={false}
            />
          ) : null}
        </div>

        {(isLoading || isPreviewLoading) && (
          <div className="pointer-events-none absolute inset-0 z-[10] flex items-center justify-center bg-black/50">
            <ShadowSpinner ariaLabel={isLoading ? "Loading video" : "Loading preview"} />
          </div>
        )}

        <div
          className={`absolute top-0 left-0 h-full w-full bg-[rgba(0,0,0,0.2)] transition-opacity duration-300 ${overlayOpacityClass}`}
          id={overlayId}
          style={{ zIndex: 2 }}
        >
          <Image
            src={thumbnail}
            alt={video.event_name || "Video"}
            fill
            loader={thumbnailLoader}
            className="z-[0] object-cover"
            sizes="(max-width: 420px) 160px, (max-width: 768px) 180px, 214px"
            quality={70}
            priority={false}
          />
          <div
            className={`absolute inset-0 m-auto flex h-[42px] w-[42px] items-center justify-center rounded-full bg-black/40 transition-all duration-300 ease-in-out max-[768px]:!h-[28px] max-[768px]:!w-[28px]`}
          >
            <PlayIcon className="ml-[3px] h-[30px] w-[30px]" />
          </div>
        </div>

        {pipThumbnailOverlayHidden && <PipOverlayLabel />}
      </div>

      <div className="pt-[6px]">
        <h3 className="font-rubik ml-[3px] line-clamp-2 !text-[12px] leading-[1.4] font-normal text-[#1A1A1A] sm:!text-[12px] md:!text-[16px] xl:!text-[16px]">
          {video?.event_name}
        </h3>
        <div className="font-rubik ml-[4px] inline-block !text-[12px] font-normal text-[#999999] sm:!text-[12px] md:!text-[14px] xl:!text-[14px]">
          {<span className="mr-2">{video.event_description}</span>}
        </div>
      </div>
      {encodedId && <span style={{ display: "none" }}>{encodedId}</span>}
    </button>
  );
}
